// Utility function to easily update photo lists
const getPhotographyPhotos = () => [
  "/photog/IMG_1628.JPG",
  "/photog/IMG_1345.JPG",
  "/photog/IMG_1210.JPG",
  "/photog/bdedfc6c-6d83-4ed2-9f96-e26148f3a64c_rw_3840.jpg",
  "/photog/6cce34c4-dfb2-4c3c-b73b-63a4982375f1_rw_3840.jpg",
  "/photog/2d591fc8-3115-4fc1-9a31-ec441b461cab_rw_3840.jpg",
  "/photog/ff6e7e9b-4749-4fdb-aa99-1519b95fd252_rw_3840.jpg",
  "/photog/MVI_1252.gif",
  "/photog/20280f1f-99e2-4e96-beba-4b7ea8417ac6_rw_3840.JPG",
  "/photog/a2e556a9-f445-4ead-8192-d628fe9c1af4_rw_1920.PNG",
  "/photog/E900626E-D221-41CD-90B9-F113B39B0FF7_Original.jpg"
]

const getModelingPhotos = () => [
  "/modeling/lblue.gif",
  "/modeling/82ee3d6f-7eed-4f1c-bf7d-8f4d17038bc0.png",
  "/modeling/33b7ab34-7ca4-4f99-9e49-942259174b84_rw_1920.gif",
  "/modeling/ce817ae9-e422-4374-a1e0-d6ff7361c725_rw_1200.jpeg",
  "/modeling/pink.gif",
  "/modeling/941aba01-8c62-4a05-b78f-2310c51f76de_rw_1920.gif",
  "/modeling/mushroom05_use.png"
]

import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { ChevronLeft, ChevronRight, Camera, Box } from 'lucide-react'
import { useState, useEffect } from 'react'

const Fun = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const [photoIndex, setPhotoIndex] = useState(0)
  const [modelIndex, setModelIndex] = useState(0)

  // Get photo arrays
  const photographyPhotos = getPhotographyPhotos();
  const modelingPhotos = getModelingPhotos();

  // Auto-rotate carousels
  useEffect(() => {
    if (!inView) return

    const photoInterval = setInterval(() => {
      setPhotoIndex((prev) => (prev + 1) % photographyPhotos.length)
    }, 3000)

    const modelInterval = setInterval(() => {
      setModelIndex((prev) => (prev + 1) % modelingPhotos.length)
    }, 6000)

    return () => {
      clearInterval(photoInterval)
      clearInterval(modelInterval)
    }
  }, [inView])

  const funFacts = [
    "500+ day Linkedin games streak",
    "road-tripped Iceland by campervan!", 
    "♥︎ Studio Ghibli movies",
    "love mental math [mathheads.net]",
    "jasmine fruit green tea addict",
    "160+ wpm typing speed (usually)"
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  const nextPhoto = () => {
    setPhotoIndex((prev) => (prev + 1) % photographyPhotos.length)
  }

  const prevPhoto = () => {
    setPhotoIndex((prev) => (prev - 1 + photographyPhotos.length) % photographyPhotos.length)
  }

  const nextModel = () => {
    setModelIndex((prev) => (prev + 1) % modelingPhotos.length)
  }

  const prevModel = () => {
    setModelIndex((prev) => (prev - 1 + modelingPhotos.length) % modelingPhotos.length)
  }

  return (
    <div className="container-max pt-40" ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="space-y-16"
      >
        {/* Header */}
        <motion.div
          variants={itemVariants}
          className="space-y-4"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary"
            style={{ marginBottom: '1rem' }}>
            fun & interests
          </h2>
          <p className="text-xl text-muted font-mono max-w-2xl">
            about & other thoughts
          </p>
        </motion.div>

        {/* about me Section */}
        <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-8 items-start">
          {/* About Text */}
          <div className="md:col-span-2 space-y-4 text-left order-2 md:order-1">
            <h3 className="text-2xl font-semibold text-primary">about me</h3>
            <p className="text-primary leading-relaxed">
              Hi! I'm Claire, and I'm a rising junior studying Computer Science and Applied Math @ Brown University! (i'm a bay area native :))<br /> <br />
              I love blending technical approaches & creativity to tackle multifaceted, complex problems. 
              Outside of class & work, you can find me eagerly playing strategic thinking games, creating 3D models for Hack@Brown, or capturing moments of daily life with my camera.
              I thrive in environments that foster collaboration and innovation, and I'm always looking for new opportunities to learn and grow! 
            </p>
          </div>
          {/* Photo */}
          <div className="flex justify-center md:justify-end order-1 md:order-2">
            <img
              src="/pfp4.jpg"
              alt="Claire Luo"
              className="w-56 h-56 object-cover rounded-2xl shadow-lg border-4 border-white/60"
            />
          </div>
        </motion.div>

        {/* Fun Facts */}
        <motion.div variants={itemVariants} className="space-y-6">
          <h3 className="text-2xl font-semibold text-primary">fun facts</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {funFacts.map((fact, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -4 }}
                className="p-4 rounded-xl bg-white/40 backdrop-blur border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all text-center"
              >
                <p className="text-primary text-md font-medium leading-relaxed">
                  {fact}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Spacer for guaranteed separation */}
        <div className="h-10 md:h-10"></div>

        {/* Photo Carousels */}
        <motion.div variants={itemVariants} className="space-y-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Photography Carousel */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Camera className="text-primary" size={24} />
                <h3 className="text-2xl font-semibold text-primary">photography</h3>
              </div>
              <p className="text-primary/80 text-lg font-mono">
                capturing moments & exploring perspectives
              </p>
              <div className="relative group">
                <div className="relative h-96 flex items-center justify-center">
                  {/* Previous Image (Left Half) */}
                  <div className="absolute left-0 flex items-center">
                    <motion.div
                      className="relative overflow-hidden rounded-lg"
                      style={{
                        transform: 'translateX(30%)',
                        zIndex: 1
                      }}
                    >
                      <img
                        src={photographyPhotos[(photoIndex - 1 + photographyPhotos.length) % photographyPhotos.length]}
                        alt="Previous photo"
                        className="w-56 h-56 object-cover"
                        style={{
                          opacity: 0.8,
                          transform: 'translateX(0%)',
                          filter: 'blur(2px)'
                        }}
                      />
                    </motion.div>
                  </div>

                  {/* Current Image (Center) on Static Translucent Card */}
                  <div className="relative z-10">
                    <div className="backdrop-blur-md bg-white/20 shadow-xl rounded-2xl p-4">
                      <motion.img
                        key={photoIndex}
                        src={photographyPhotos[photoIndex]}
                        alt={`Photography ${photoIndex + 1}`}
                        className="w-72 h-72 object-cover rounded-xl"
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -20 }}
                        transition={{ 
                          duration: 0.6, 
                          ease: [0.68, -0.55, 0.265, 1.55],
                          scale: { duration: 0.4 }
                        }}
                      />
                    </div>
                  </div>

                  {/* Next Image (Right Half) */}
                  <div className="absolute right-0 flex items-center">
                    <motion.div
                      className="relative overflow-hidden rounded-lg"
                      style={{
                        transform: 'translateX(-30%)',
                        zIndex: 1
                      }}
                    >
                      <img
                        src={photographyPhotos[(photoIndex + 1) % photographyPhotos.length]}
                        alt="Next photo"
                        className="w-56 h-56 object-cover"
                        style={{
                          opacity: 0.8,
                          transform: 'translateX(0%)',
                          filter: 'blur(2px)'
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
                
                {/* Navigation Buttons */}
                <button
                  onClick={prevPhoto}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full backdrop-blur-md bg-white/20 shadow-lg text-primary hover:bg-white/30 transition-all opacity-0 group-hover:opacity-100 z-20 border border-white/20"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full backdrop-blur-md bg-white/20 shadow-lg text-primary hover:bg-white/30 transition-all opacity-0 group-hover:opacity-100 z-20 border border-white/20"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
              
              {/* Dots - Below Frame */}
              <div className="flex justify-center gap-2 mt-4">
                {photographyPhotos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setPhotoIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === photoIndex ? 'bg-primary' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* 3D Modeling Carousel */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Box className="text-primary" size={24} />
                <h3 className="text-2xl font-semibold text-primary">3D modeling</h3>
              </div>
              <p className="text-primary/80 text-lg font-mono">
                bringing ideas to life in three dimensions
              </p>
              <div className="relative group">
                <div className="relative h-96 flex items-center justify-center">
                  {/* Previous Image (Left Half) */}
                  <div className="absolute left-0 flex items-center">
                    <motion.div
                      className="relative overflow-hidden rounded-lg"
                      style={{
                        transform: 'translateX(30%)',
                        zIndex: 1
                      }}
                    >
                      <img
                        src={modelingPhotos[(modelIndex - 1 + modelingPhotos.length) % modelingPhotos.length]}
                        alt="Previous model"
                        className="w-56 h-56 object-cover"
                        style={{
                          opacity: 0.8,
                          transform: 'translateX(0%)',
                          filter: 'blur(2px)'
                        }}
                      />
                    </motion.div>
                  </div>

                  {/* Current Image (Center) on Static Translucent Card */}
                  <div className="relative z-10">
                    <div className="backdrop-blur-md bg-white/20 shadow-xl rounded-2xl p-4">
                      <motion.img
                        key={modelIndex}
                        src={modelingPhotos[modelIndex]}
                        alt={`3D Model ${modelIndex + 1}`}
                        className="w-72 h-72 object-cover rounded-xl"
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -20 }}
                        transition={{ 
                          duration: 0.6, 
                          ease: [0.68, -0.55, 0.265, 1.55],
                          scale: { duration: 0.4 }
                        }}
                      />
                    </div>
                  </div>

                  {/* Next Image (Right Half) */}
                  <div className="absolute right-0 flex items-center">
                    <motion.div
                      className="relative overflow-hidden rounded-lg"
                      style={{
                        transform: 'translateX(-30%)',
                        zIndex: 1
                      }}
                    >
                      <img
                        src={modelingPhotos[(modelIndex + 1) % modelingPhotos.length]}
                        alt="Next model"
                        className="w-56 h-56 object-cover"
                        style={{
                          opacity: 0.8,
                          transform: 'translateX(0%)',
                          filter: 'blur(2px)'
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
                
                {/* Navigation Buttons */}
                <button
                  onClick={prevModel}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full backdrop-blur-md bg-white/20 shadow-lg text-primary hover:bg-white/30 transition-all opacity-0 group-hover:opacity-100 z-20 border border-white/20"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextModel}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full backdrop-blur-md bg-white/20 shadow-lg text-primary hover:bg-white/30 transition-all opacity-0 group-hover:opacity-100 z-20 border border-white/20"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
              
              {/* Dots - Below Frame */}
              <div className="flex justify-center gap-2 mt-4">
                {modelingPhotos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setModelIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === modelIndex ? 'bg-primary' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default Fun 